/**
 * A Proposta dentro da Oportunidade (DEMO-03, telas/Oportunidade.tsx): resumo
 * da versão VIGENTE, aba Propostas (versões, criar, nova versão) e "Registrar
 * resultado" 🔒: registrado UMA vez na versão vigente da Proposta (Oportunidade sincronizada, Pedido gerado
 * no ganho, na mesma transação); sem proposta enviada, só Perdida na Oportunidade (09/10/2026).
 */
import { useState } from 'react';
import { api } from '../../api.ts';
import { navegar } from '../../rotas.ts';
import { useSessao } from '../../sessao.tsx';
import { Estado, Modal, ResumoIndicadores } from '../ui.tsx';
import { dataBr, estagioAberto, nomeEstagio, TIPOS } from '../../../../backend/src/compartilhado/oportunidades.ts';
import { SITUACOES_VERSAO, aplicarLinhas, economiaVersao, linhasIniciais, linhasResultadoDaVersao, motivoPerdaExigeObservacao, rascunhoDe, resumoLinhas, validarLinhas, versaoVigente, type LinhaInformada, type Versao } from '../../../../backend/src/compartilhado/propostas.ts';
import { ProvedorProposta, useProposta, type DadosProposta } from './contexto.tsx';
import { PainelProposta } from './PainelProposta.tsx';
import { TabelaResultado } from './ResultadoLinhas.tsx';
import { moeda2, pct } from './formatos.ts';
import { CampoData, hojeIso } from '../CampoData.tsx';
import { AbrangenciaOc, ACEITE_ARQUIVO_OC, arquivoParaApi } from '../Pedidos.tsx';

/** Resumo da proposta vigente na Oportunidade (ou "Sem proposta ainda"). */
export function ResumoVigente({ oportunidadeId, valorEstimado, previsao }: { oportunidadeId: string; valorEstimado: number | null; previsao: string | null }) {
  const { pode } = useSessao();
  if (!pode('visualizar', 'proposta')) return <SemProposta valorEstimado={valorEstimado} previsao={previsao} />;
  return (
    <ProvedorProposta oportunidadeId={oportunidadeId}>
      <ResumoInterno valorEstimado={valorEstimado} previsao={previsao} />
    </ProvedorProposta>
  );
}
function SemProposta({ valorEstimado, previsao }: { valorEstimado: number | null; previsao: string | null }) {
  return (
    <section className="cartao-resumo resumo-vigente" data-teste="resumo-vigente">
      <ResumoIndicadores compacto itens={[{ rotulo: 'Proposta', valor: 'Sem proposta ainda' }, { rotulo: 'Valor estimado', valor: valorEstimado === null ? 'Não determinado na origem' : moeda2(valorEstimado) }, { rotulo: 'Previsão de fechamento', valor: previsao ? dataBr(previsao) : '—' }]} />
    </section>
  );
}
function ResumoInterno({ valorEstimado, previsao }: { valorEstimado: number | null; previsao: string | null }) {
  const { dados } = useProposta();
  const p = dados.proposta;
  if (!p) return <SemProposta valorEstimado={valorEstimado} previsao={previsao} />;
  const v = versaoVigente(p);
  const r = rascunhoDe(p);
  const base = `/oportunidades/${dados.oportunidade.id}/propostas/${p.id}`;
  return (
    <>
      <PainelProposta
        proposta={p}
        v={v}
        acoes={
          <>
            <a className="botao-secundario pequeno" href={`#${base}/v/${v.numero}`} data-teste="abrir-proposta">
              Abrir proposta
            </a>
            <a className="botao-secundario pequeno" href={`#${base}/pdf/${v.numero}`}>
              Visualizar PDF
            </a>
          </>
        }
      />
      {r && r.numero !== v.numero && <p className="texto-secundario">v{r.numero} em elaboração (rascunho) — ainda não é a vigente.</p>}
    </>
  );
}

/** Aba Propostas: sem proposta → "Nova proposta"; com proposta → versões (abrir, gerar nova versão, PDF). */
export function PropostasDaOportunidade({ oportunidadeId, aoMudar }: { oportunidadeId: string; aoMudar: () => void }) {
  const { pode } = useSessao();
  if (!pode('visualizar', 'proposta')) return <Estado tipo="bloqueado" titulo="Sem permissão para ver Propostas" />;
  return (
    <ProvedorProposta oportunidadeId={oportunidadeId}>
      <PropostasInterno aoMudar={aoMudar} />
    </ProvedorProposta>
  );
}
function PropostasInterno({ aoMudar }: { aoMudar: () => void }) {
  const { d, dados, aplicar } = useProposta();
  const { pode } = useSessao();
  const [erro, setErro] = useState('');
  const o = dados.oportunidade;
  const p = dados.proposta;
  const aberta = estagioAberto(o.estagio);
  if (!p)
    return (
      <div className="vazio" data-teste="propostas-oportunidade">
        <p>Nenhuma proposta ainda. A parte econômica (itens, custos, margens, frete, faturamento e entrega) é preenchida dentro da Proposta.</p>
        {erro && (
          <p className="aviso aviso-erro" role="alert">
            {erro}
          </p>
        )}
        {aberta && pode('criar', 'proposta') && (
          <button
            type="button"
            className="botao-primario"
            onClick={async () => {
              try {
                const r = await api.post<{ id: string }>(`/api/oportunidades/${o.id}/proposta`, {});
                aoMudar();
                navegar(`/oportunidades/${o.id}/propostas/${r.id}/v/1`);
              } catch (e) {
                setErro((e as Error).message);
              }
            }}
            data-teste="nova-proposta"
          >
            Nova proposta de {TIPOS[o.tipo]}
          </button>
        )}
      </div>
    );
  const vig = versaoVigente(p);
  const r = rascunhoDe(p);
  const base = `/oportunidades/${o.id}/propostas/${p.id}`;
  return (
    <section data-teste="propostas-oportunidade">
      <h2>Proposta {p.numero}</h2>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <div className="rolagem">
        <table className="tabela" data-teste="versoes">
          <thead>
            <tr>
              <th>Versão</th>
              <th>Status</th>
              <th>Data</th>
              <th>Validade</th>
              <th>Total</th>
              <th>Margem</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {p.versoes
              .slice()
              .reverse()
              .map((v) => {
                const e = economiaVersao(d, v);
                const vigente = v.numero === vig.numero;
                return (
                  <tr key={v.numero} className={vigente ? 'linha-vigente' : v.situacao === 'substituida' ? 'linha-historico' : ''} data-teste={`versao-${v.numero}`}>
                    <td>
                      v{v.numero} {vigente ? <span className="selo selo-destaque">vigente</span> : v.situacao === 'rascunho' ? <span className="selo selo-alerta">em elaboração</span> : <span className="selo selo-neutro">histórico</span>}
                    </td>
                    <td>{SITUACOES_VERSAO[v.situacao]}</td>
                    <td>{dataBr(v.data)}</td>
                    <td>{dataBr(v.validade)}</td>
                    <td className="nowrap">{moeda2(e.totalFinal)}</td>
                    <td className="nowrap">
                      {moeda2(e.bruta)} ({pct(e.percentual)})
                    </td>
                    <td className="nowrap acoes-linha">
                      <a className="botao-secundario pequeno" href={`#${base}/v/${v.numero}`} data-teste={`abrir-versao-${v.numero}`}>
                        {v.situacao === 'rascunho' ? 'Abrir e editar' : 'Abrir'}
                      </a>
                      {vigente && !r && aberta && v.situacao !== 'rascunho' && pode('editar', 'proposta') && (
                        <button
                          type="button"
                          className="botao-secundario pequeno"
                          onClick={async () => {
                            try {
                              const x = await api.post<{ numero: number }>(`/api/propostas/${p.id}/nova-versao`, {});
                              aplicar(await api.get<DadosProposta>(`/api/oportunidades/${o.id}/proposta`));
                              navegar(`${base}/v/${x.numero}`);
                            } catch (e) {
                              setErro((e as Error).message);
                            }
                          }}
                        >
                          Gerar nova versão
                        </button>
                      )}
                      <a className="botao-secundario pequeno" href={`#${base}/pdf/${v.numero}`}>
                        Visualizar PDF
                      </a>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Motivo da perda (lista) + observação — "Outro" exige a observação (tela e servidor; decisão de 09/10/2026). */
function CamposPerda({ motivos, motivo, setMotivo, obs, setObs }: { motivos: string[]; motivo: string; setMotivo: (x: string) => void; obs: string; setObs: (x: string) => void }) {
  const exigeObs = motivoPerdaExigeObservacao(motivo);
  return (
    <>
      {motivos.length === 0 && <p className="aviso aviso-alerta">A lista de motivos da perda não está configurada neste ambiente (Parâmetros).</p>}
      <label className="campo">
        Motivo (obrigatório)
        <select value={motivo} onChange={(e) => setMotivo(e.target.value)} required data-teste="motivo-perda">
          <option value="">Selecione…</option>
          {motivos.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
      </label>
      <label className="campo">
        {exigeObs ? 'Observação (obrigatória para o motivo “Outro”)' : 'Observação'}
        <textarea rows={3} value={obs} onChange={(e) => setObs(e.target.value)} maxLength={1000} required={exigeObs} data-teste="observacao-perda" />
      </label>
    </>
  );
}
const perdaIncompleta = (motivo: string, obs: string) => !motivo || (motivoPerdaExigeObservacao(motivo) && !obs.trim());

/**
 * Oportunidade SEM proposta enviada (ex.: perdida na prospecção): só "Perdida" 🔒, com motivo da lista.
 * Com proposta enviada, o resultado é registrado uma única vez na Proposta (versão vigente).
 */
export function RegistrarPerdaSemProposta({ oportunidade, motivos, aoFechar, aoSalvar }: { oportunidade: { id: string; numero: string; atualizado_em?: string }; motivos: string[]; aoFechar: () => void; aoSalvar: () => void }) {
  const [motivo, setMotivo] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const confirmar = async () => {
    if (perdaIncompleta(motivo, obs)) return;
    setErro('');
    setEnviando(true);
    try {
      await api.post(`/api/oportunidades/${oportunidade.id}/resultado`, { resultado: 'perdida', motivo, observacao: obs, atualizado_em: oportunidade.atualizado_em });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={`Registrar resultado — #${oportunidade.numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" onClick={() => void confirmar()} disabled={perdaIncompleta(motivo, obs) || enviando} data-teste="confirmar-resultado">
            Confirmar perda
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <p className="aviso aviso-info" data-teste="perda-sem-proposta">
        Sem proposta enviada: a Oportunidade só pode ser encerrada como <strong>Perdida</strong>. Ganha e Ganha parcialmente são registradas na Proposta enviada, que gera o Pedido.
      </p>
      <CamposPerda motivos={motivos} motivo={motivo} setMotivo={setMotivo} obs={obs} setObs={setObs} />
    </Modal>
  );
}

/**
 * FECHAMENTO ÚNICO 🔒 na versão VIGENTE da Proposta (decisão de 09/10/2026): Ganha, Ganha parcialmente ou Perdida,
 * numa só confirmação. A Oportunidade é atualizada automaticamente e, no ganho, o Pedido congelado é gerado da
 * versão aceita (só as quantidades ganhas), com a OC do Cliente ou "ainda não enviou". O servidor confere que esta
 * ainda é a versão vigente (tela desatualizada é recusada).
 */
export function RegistrarResultadoProposta({ propostaId, v, numeroOp, motivos, aoFechar, aoSalvar }: { propostaId: string; v: Versao; numeroOp: string; motivos: string[]; aoFechar: () => void; aoSalvar: () => void }) {
  const [r, setR] = useState<'ganha' | 'ganha_parcialmente' | 'perdida'>('ganha');
  const [motivo, setMotivo] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState('');
  const todasGanhas = (): LinhaInformada[] => linhasResultadoDaVersao(v).map((l) => ({ id: l.id, ganha: l.quantidade, perdida: 0, motivo: '', observacao: '' }));
  const iniciais = () => (linhasResultadoDaVersao(v).some((l) => l.quantidadeAceita || l.quantidadePerdida) ? linhasIniciais(v) : todasGanhas());
  const [linhas, setLinhas] = useState<LinhaInformada[]>(todasGanhas);
  const aplicadas = resumoLinhas(aplicarLinhas(v, linhas));
  const invalido = validarLinhas(v, linhas).length > 0;
  const semGanho = r !== 'perdida' && (!aplicadas.algumGanho || (aplicadas.conjunto && !aplicadas.tudoGanho) || invalido);
  const [oc, setOc] = useState({ pendente: false, numero: '', dataRecebimento: hojeIso(), observacao: '' });
  // Pacote 4: a OC declara o que abrange (o Pedido inteiro ou itens ganhos escolhidos) — nada é suposto
  const [abrangencia, setAbrangencia] = useState<'' | 'pedido' | 'itens'>('');
  const [itensOc, setItensOc] = useState<string[]>([]);
  const ganhasOc = linhasResultadoDaVersao(v).filter((l) => r === 'ganha' || (linhas.find((x) => x.id === l.id)?.ganha ?? 0) > 0);
  const nomeLinha = (l: { id: string } & Record<string, unknown>) => String((l.descricao as string) || (l.produtoCopia as { nome?: string } | undefined)?.nome || l.id);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const itensOcValidos = itensOc.filter((x) => ganhasOc.some((l) => l.id === x));
  const ocIncompleta = r !== 'perdida' && !oc.pendente && (!oc.numero.trim() || !oc.dataRecebimento || !abrangencia || (abrangencia === 'itens' && itensOcValidos.length === 0));
  const bloqueado = r === 'perdida' ? perdaIncompleta(motivo, obs) : semGanho || ocIncompleta;
  const confirmar = async () => {
    if (bloqueado) return;
    setErro('');
    setEnviando(true);
    try {
      const ocCorpo = oc.pendente ? { pendente: true } : { numero: oc.numero, data_recebimento: oc.dataRecebimento, observacao: oc.observacao, arquivo: await arquivoParaApi(arquivo), abrangencia, ...(abrangencia === 'itens' ? { itens: itensOcValidos } : {}) };
      const corpo = r === 'perdida' ? { resultado: r, versao: v.numero, motivo, observacao: obs } : { resultado: r, versao: v.numero, linhas: r === 'ganha' ? todasGanhas() : linhas, oc: ocCorpo };
      const res = await api.post<{ pedidos: string[] }>(`/api/propostas/${propostaId}/resultado`, corpo);
      aoSalvar();
      // Como na DEMO-03: com o Pedido gerado, abre o Pedido.
      if (res.pedidos?.length) navegar(`/pedidos/${res.pedidos[0]}`);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={`Registrar resultado — v${v.numero} (vigente) · #${numeroOp}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" onClick={() => void confirmar()} disabled={bloqueado || enviando} title={ocIncompleta ? 'Informe o número e a data de recebimento da OC, ou marque “Cliente ainda não enviou a OC”' : undefined} data-teste="confirmar-resultado">
            Confirmar resultado
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <p className="aviso aviso-info" data-teste="fechamento-unico">
        O resultado é registrado uma única vez, nesta versão vigente (v{v.numero}). A Oportunidade #{numeroOp} é atualizada automaticamente{r === 'perdida' ? ' para Perdida, com o mesmo motivo' : ' e o Pedido é gerado'}.
      </p>
      <fieldset className="opcoes">
        <legend>Resultado</legend>
        {(['ganha', 'ganha_parcialmente', 'perdida'] as const).map((x) => (
          <label key={x} className="opcao">
            <input
              type="radio"
              name="resultado"
              value={x}
              checked={r === x}
              onChange={() => {
                setR(x);
                if (x === 'ganha') setLinhas(todasGanhas());
                if (x === 'ganha_parcialmente') setLinhas(iniciais());
              }}
              data-teste={`resultado-${x}`}
            />{' '}
            {nomeEstagio(x)}
          </label>
        ))}
      </fieldset>
      {r === 'perdida' ? (
        <CamposPerda motivos={motivos} motivo={motivo} setMotivo={setMotivo} obs={obs} setObs={setObs} />
      ) : (
        <>
          <section data-teste="itens-ganhos">
            <h3>Resultado por linha da v{v.numero} (conferência antes do Pedido)</h3>
            {r === 'ganha' ? <p className="texto-secundario">Ganha: todas as linhas ganhas na quantidade proposta. Para ganho parcial, escolha “Ganha parcialmente”.</p> : <TabelaResultado v={v} linhas={linhas} setLinhas={setLinhas} motivos={motivos} />}
          </section>
          {semGanho && <Estado tipo="bloqueado" titulo="Para registrar o ganho" itens={[aplicadas.conjunto && !aplicadas.tudoGanho ? 'Locação tem preço fechado do conjunto: aceite parcial exige nova versão com o valor revisado.' : invalido ? 'Corrija as linhas indicadas acima.' : 'Informe ao menos uma quantidade ganha (ou registre como Perdida).']} teste="sem-ganho" />}
          <fieldset className="form-grade" data-teste="oc-cliente-resultado">
            <legend>OC do Cliente</legend>
            <label className="opcao largura-total">
              <input type="checkbox" checked={oc.pendente} onChange={(e) => setOc({ ...oc, pendente: e.target.checked })} data-teste="oc-pendente" /> Cliente ainda não enviou a OC (vincular depois, no Pedido)
            </label>
            {!oc.pendente && (
              <>
                <label>
                  Número da OC
                  <input value={oc.numero} onChange={(e) => setOc({ ...oc, numero: e.target.value })} maxLength={80} data-teste="oc-numero" />
                </label>
                <CampoData rotulo="Data de recebimento" valor={oc.dataRecebimento} aoMudar={(x) => setOc({ ...oc, dataRecebimento: x })} max={hojeIso()} teste="oc-data" obrigatorio />
                <label>
                  Arquivo (PDF recomendado)
                  <input type="file" accept={ACEITE_ARQUIVO_OC} onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} data-teste="oc-arquivo" />
                  <small className="ajuda">{arquivo ? `Selecionado: ${arquivo.name} — acompanha o Pedido, sem novo upload.` : 'PDF, imagem, Office ou e-mail; o tipo é conferido pelo conteúdo.'}</small>
                </label>
                <label className="largura-total">
                  Observação
                  <input value={oc.observacao} onChange={(e) => setOc({ ...oc, observacao: e.target.value })} data-teste="oc-observacao" />
                </label>
                <AbrangenciaOc abrangencia={abrangencia} setAbrangencia={setAbrangencia} itens={ganhasOc.map((l) => ({ id: l.id, nome: nomeLinha(l as never) }))} escolhidos={itensOcValidos} setEscolhidos={setItensOc} />
              </>
            )}
          </fieldset>
          {ocIncompleta && <Estado tipo="bloqueado" titulo="OC do Cliente" itens={['Informe o número, a data de recebimento e o que a OC abrange (o Pedido inteiro ou os itens), ou marque “Cliente ainda não enviou a OC”.']} teste="oc-falta" />}
          <p className="texto-secundario">
            Será gerado o Pedido (PED-, número emitido ao confirmar) a partir da v{v.numero}, com cliente, entidade, faturamento, entrega, itens {r === 'ganha' ? '(todos)' : 'ganhos'}, quantidades, custos, preços de venda, margens, frete e condições CONGELADOS — sem redigitação. O tipo da Oportunidade é copiado e congelado.
          </p>
        </>
      )}
    </Modal>
  );
}
